import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { isFirefoxOrSafari } from '../common/utils';
import { device } from 'saur-design-sys-web/lib/Theme';
import Word from 'saur-design-sys-web/lib/Atoms/Word/Word';

const fadeSlideIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(-100%);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const fadeSlideOut = keyframes`
    from {
        opacity: 1;
        transform: translateY(0);
        max-height: 60px;
    }
    to {
        opacity: 0;
        transform: translateY(-100%);
        max-height: 0;
    }
`;

const BannerContainer = styled.header<{ $isClosing: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    display: flex !important;
    justify-content: center;
    background: #ffcc00;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    animation: ${props => props.$isClosing ? fadeSlideOut : fadeSlideIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    
    @media ${device.mobile} {
        position: relative;
    }
`;

const BannerContent = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 12px 20px;
    display: flex !important;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 48px;
    
    @media ${device.mobile} {
        padding: 10px 16px;
        gap: 10px;
        min-height: 44px;
    }
`;

const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #000;
    
    svg {
        width: 20px;
        height: 20px;
        stroke-width: 2.5;
    }
    
    @media ${device.mobile} {
        svg {
            width: 18px;
            height: 18px;
        }
    }
`;

const MessageContainer = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .sds-word {
        color: #ffffff;
        line-height: 1.5;
        font-weight: 400;
        font-size: 14px;
        text-align: center;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
    
    @media ${device.mobile} {
        .sds-word {
            font-size: 13px;
            line-height: 1.4;
        }
    }
`;

const CloseButton = styled.button`
    background: transparent;
    border: none;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    color: #000;
    font-size: 22px;
    line-height: 1;
    border-radius: 4px;
    padding: 0;
    
    &:hover {
        background: rgba(255, 255, 255, 0.15);
        color: #ffffff;
    }
    
    &:active {
        background: rgba(255, 255, 255, 0.25);
        transform: scale(0.95);
    }
    
    &:focus-visible {
        outline: 2px solid rgba(255, 255, 255, 0.8);
        outline-offset: 2px;
    }
    
    @media ${device.mobile} {
        width: 26px;
        height: 26px;
        font-size: 20px;
    }
`;

const BrowserWarning: FC = () => {
    const { t } = useTranslation();
    const [shouldDisplay, setShouldDisplay] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        setShouldDisplay(isFirefoxOrSafari());
    }, []);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setShouldDisplay(false);
        }, 500); // Match animation duration
    };

    if (!shouldDisplay) {
        return null;
    }

    return (
        <BannerContainer $isClosing={isClosing} aria-live="polite">
            <BannerContent>
                <IconContainer aria-hidden="true">
                    <svg 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                </IconContainer>
                <MessageContainer>
                    <Word
                        value={t('browser_warning')}
                        textStyle='normalReg'
                        variant='span'
                    />
                </MessageContainer>
                <CloseButton 
                    onClick={handleClose} 
                    aria-label="Fermer le message d'information"
                    title="Fermer"
                >
                    ✕
                </CloseButton>
            </BannerContent>
        </BannerContainer>
    );
};

export default BrowserWarning;
